import { useMemo } from "react";
import { createColumnHelper, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { DISPLAY_DATE_WITH_TIME } from "@constants/date-formats";
import { formatDateToString } from "@utils/date-util";
import { IdwTable } from "../../common/idw-table/idw-table";
import { FormattedAlertEventHistoryCause } from "@idw-types/data/alert-event-history";
import { resources } from "@constants/hardcoded-strings";
import styles from "./alert-event-incidents-table-container.less";

type AlertEventIncidentsTableContainerProps = {
    dateTriggered: Date;
    cause: FormattedAlertEventHistoryCause;
};

type AlertEventIncidentsTableDataRow = {
    dateTriggered: Date;
    serviceName: string;
    workflowName: string;
    credentials: string;
    causeDescription: string;
    asis: string;
};

export const AlertEventIncidentsTableContainer = (props: AlertEventIncidentsTableContainerProps) => {
    const { cause, dateTriggered } = props;

    const alertEventIncidentsTableData: Array<AlertEventIncidentsTableDataRow> = useMemo(() => {
        let causePrefix = "";

        if (cause?.assertion) {
            causePrefix = cause.assertion.shortName;
        } else if (cause?.assertionIDs.length > 0) {
            causePrefix = `Assertion ID: ${cause.assertionIDs[0]}`;
        } else if (cause?.scoreAttribute) {
            causePrefix = cause.scoreAttribute.name
        } else if (cause?.scoreAttributeIDs.length > 0) {
            causePrefix = `Attribute ID: ${cause.scoreAttributeIDs[0]}`;
        } else if (cause?.policy) {
            causePrefix = cause.policy.policyName
        } else if (cause?.policyIDs.length > 0) {
            causePrefix = `Policy ID: ${cause.policyIDs[0]}`;
        }

        let causeDescription = resources.common.notFound;
        if (causePrefix && cause?.values.length > 0) {
            causeDescription = `${causePrefix} = ${cause?.values[0]}`;
        }

        return [{
            dateTriggered: dateTriggered,
            serviceName: cause?.rpService?.serviceName || resources.common.notFound,
            workflowName: cause?.workflow?.name || resources.common.notFound,
            credentials: cause?.credentials?.join(", ") || resources.common.notFound,
            causeDescription: causeDescription,
            asis: cause?.asis?.join(", ") || resources.common.notFound
        }];
    }, [cause]);

    const columnHelper = createColumnHelper<AlertEventIncidentsTableDataRow>();

    const columns = useMemo(() => ([
        columnHelper.display({
            id: "order",
            header: "#",
            cell: () => <span>1</span>
        }),
        columnHelper.accessor("dateTriggered", {
            header: resources.tables.alertEventIncidents.columns.dateTriggered.header,
            cell: (props) => (props.getValue() ? <span>{formatDateToString(props.getValue(), DISPLAY_DATE_WITH_TIME)}</span> : null)
        }),
        columnHelper.accessor("workflowName", {
            header: resources.tables.alertEventIncidents.columns.workflow.header
        }),
        columnHelper.accessor("serviceName", {
            header: resources.tables.alertEventIncidents.columns.service.header
        }),
        columnHelper.accessor("credentials", {
            header: resources.tables.alertEventIncidents.columns.credential.header
        }),
        columnHelper.accessor("causeDescription", {
            header: resources.tables.alertEventIncidents.columns.cause.header
        }),
        columnHelper.accessor("asis", {
            header: resources.tables.alertEventIncidents.columns.asi.header
        }),
    ]), [alertEventIncidentsTableData]);


    const useTableResult = useReactTable({
        columns,
        enableFilters: false,
        enableSorting: false,
        sortDescFirst: false,
        data: alertEventIncidentsTableData,
        getCoreRowModel: getCoreRowModel(), 
        filterFns: null
    });

    return (
        <div className={styles.alertEventIncidentsTableContainer}>
            <IdwTable<AlertEventIncidentsTableDataRow>
                id="alert-events"
                useTableResult={useTableResult}
                headerColumnKeyPrefix={"alert-events"}
                className={styles.alertEventIncidentsTable}
            />
        </div>
    );
};